import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled || open ? "bg-[#0c0e12]/80 backdrop-blur-xl border-[#262b38]" : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-5 sm:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="Interior Box home">
          <Logo />
        </Link>
        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `group relative font-mono text-xs uppercase tracking-[0.2em] py-2 transition-colors ${
                  isActive ? "text-[#f4f5f7]" : "text-[#a7aebb] hover:text-[#f4f5f7]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  <span
                    className={`absolute left-0 -bottom-0.5 h-px bg-[#d96b27] transition-[width] duration-500 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <Link
          to="/contact"
          data-testid="nav-quote-btn"
          className="hidden lg:inline-flex items-center gap-2 bg-[#d96b27] hover:bg-[#f07d38] text-[#0c0e12] px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] transition-colors clip-frame-sm"
        >
          Start a project <ArrowUpRight className="w-4 h-4" />
        </Link>
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden w-11 h-11 flex items-center justify-center border border-[#262b38]"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.45, ease: [0.7, 0, 0.2, 1] }}
            className="lg:hidden overflow-hidden bg-[#0c0e12] border-t border-[#262b38]"
          >
            <div className="px-5 py-8 flex flex-col gap-1">
              {NAV.map((n, i) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                  className="flex items-baseline justify-between py-3 border-b border-[#262b38] font-display text-3xl font-bold"
                >
                  {n.label}
                  <span className="font-mono text-xs text-[#a7aebb]">0{i + 1}</span>
                </Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
